import { useRef, useState, type JSX, type ReactNode } from 'react';
import { Box, Button, Stack, Text } from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
// Client-side gate. Source of truth for the real enforced limit is the upload
// pipeline: src/lib/upload/uploader.ts (MAX_FILE_SIZE = 100 MB) — keep in sync so
// the caption below and this reject threshold both match what actually uploads.
const MAX_SIZE_BYTES = 100 * 1024 * 1024;
const MAX_SIZE_MB = Math.round(MAX_SIZE_BYTES / (1024 * 1024));

interface DropZoneProps {
  onDrop: (files: File[]) => void | Promise<void>;
  loading?: boolean;
  /**
   * Optional unobtrusive caption rendered directly beneath the format/size line
   * (e.g. naming/dimension rules). Kept subtle by the caller — the DropZone only
   * provides the slot.
   */
  rules?: ReactNode;
}

function filterFiles(files: File[]): { accepted: File[]; rejected: File[] } {
  const accepted: File[] = [];
  const rejected: File[] = [];
  for (const f of files) {
    if (ACCEPTED_TYPES.has(f.type) && f.size <= MAX_SIZE_BYTES) {
      accepted.push(f);
    } else {
      rejected.push(f);
    }
  }
  return { accepted, rejected };
}

export function DropZone({ onDrop, loading = false, rules }: DropZoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (files: FileList | null): void => {
    if (!files || files.length === 0) return;
    const { accepted, rejected } = filterFiles(Array.from(files));
    if (rejected.length > 0) {
      console.warn(
        '[DropZone] rejected files (wrong type or >50MB):',
        rejected.map((f) => f.name),
      );
    }
    if (accepted.length > 0) {
      void onDrop(accepted);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = (): void => setDragging(false);
  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    handleFiles(e.target.files);
    e.target.value = '';
  };
  const handleClick = (): void => {
    if (!loading) inputRef.current?.click();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };
  const handleBrowseClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    handleClick();
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="Drag images here or click to select"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        flex: 1,
        minHeight: 200,
        width: '100%',
        border: `2px dashed ${dragging ? '#b65b3c' : '#d9cdb9'}`,
        borderRadius: 12,
        background: dragging ? '#f6e4d9' : '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: '0 24px',
        textAlign: 'center',
        cursor: loading ? 'wait' : 'pointer',
        transition: 'border-color 120ms, background 120ms',
        userSelect: 'none',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        style={{ display: 'none' }}
        onChange={handleChange}
        aria-hidden="true"
        tabIndex={-1}
      />
      <Box
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#f6e4d9',
          color: '#6a2f1c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconUpload size={28} stroke={1.6} />
      </Box>
      <Stack gap={4} align="center">
        <Text
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 20,
            fontWeight: 600,
            color: '#1f1a14',
            lineHeight: 1.1,
          }}
        >
          {loading ? 'Adding to queue…' : 'Drop files here'}
        </Text>
        <Text size="xs" c="#807563">
          JPEG, PNG, WebP · up to {MAX_SIZE_MB} MB
        </Text>
        {rules}
      </Stack>
      <Button
        variant="default"
        size="sm"
        onClick={handleBrowseClick}
        style={{
          height: 34,
          borderRadius: 8,
          border: '1px solid #d9cdb9',
          background: '#ffffff',
          color: '#1f1a14',
          fontWeight: 600,
        }}
      >
        Browse files…
      </Button>
    </Box>
  );
}
